import { useCallback, useEffect, useState } from 'react'

export interface ScrollDirections {
  left: boolean
  right: boolean
  top: boolean
  bottom: boolean
}

/**
 * Hook that detects if a scrollable element can scroll horizontally or vertically
 */
export const useCanScroll = (
  scrollableContentRef: React.RefObject<HTMLElement>
): ScrollDirections => {
  const [canScroll, setCanScroll] = useState<ScrollDirections>({
    left: false,
    right: false,
    top: false,
    bottom: false
  })

  const checkScrollDirections = useCallback(() => {
    const scrollContainer = scrollableContentRef.current
    if (!scrollContainer) return

    const {
      scrollLeft,
      scrollTop,
      scrollWidth,
      scrollHeight,
      clientWidth,
      clientHeight
    } = scrollContainer

    setCanScroll({
      left: scrollLeft > 0,
      right: scrollLeft < scrollWidth - clientWidth,
      top: scrollTop > 0,
      bottom: scrollTop < scrollHeight - clientHeight
    })
  }, [scrollableContentRef])

  useEffect(() => {
    const scrollContainer = scrollableContentRef.current
    if (!scrollContainer) return

    checkScrollDirections()

    scrollContainer.addEventListener('scroll', checkScrollDirections)
    window.addEventListener('resize', checkScrollDirections)

    const resizeObserver = new ResizeObserver(checkScrollDirections)
    resizeObserver.observe(scrollContainer)

    return () => {
      scrollContainer.removeEventListener('scroll', checkScrollDirections)
      window.removeEventListener('resize', checkScrollDirections)
      resizeObserver.disconnect()
    }
  }, [scrollableContentRef, checkScrollDirections])

  return canScroll
}
